import { localeFormatterHelper } from '../formatter/localeFormatter.helper.ts';
import { GridSortModel } from '@mui/x-data-grid/models/gridSortModel';

/**
 * Coerce an arbitrary cell value into a string that `localeCompare` can safely
 * consume. Guards the string-sort branch against non-string values
 * (WAWIASSIST-22P: an optimistic update stored `printedDateTime` as a `Date`
 * object while server rows held strings, so sorting that grid column threw
 * "valA.localeCompare is not a function"). A `Date` is converted to its ISO
 * form so it still orders chronologically; `null`/`undefined` become `''`.
 */
const toSortableString = (value: unknown): string => {
  if (value == null) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  return String(value);
};

class DataMethods {
  private resultData: TypeData = [];
  constructor(data: TypeData) {
    this.resultData = data;
  }
  search({ search, fields }: ISearchOptions): this {
    this.resultData = search
      ? (this.resultData?.reduce((acc, item) => {
          for (const el of fields) {
            let newEl: string;
            if (Array.isArray(el)) {
              newEl = item[el[0]] ? localeFormatterHelper.formatDate(item[el[0]], el[1]) : '';
            } else newEl = item[el] ? String(item[el])?.toLowerCase() : '';
            if (newEl.includes(search?.toLowerCase())) {
              acc.push(item);
              break;
            }
          }
          return acc;
        }, []) as [])
      : this.resultData;
    return this;
  }
  sort({ sortModel, callback }: ISortOptions): this {
    const sortArr: TypeData = [...this.resultData];
    if (sortModel.length) {
      // sorting may happen in column where we render "concatenated values"
      // then in sortModel.field we have separator ";" and we sort by "concatenated value"
      const sortFields = sortModel[0].field.split(';');
      const isNumberSorting =
        sortFields.length === 1 && !!sortArr.find((el) => typeof el[sortFields[0]] === 'number');

      sortArr.sort((a, b) => {
        if (isNumberSorting) {
          return (
            (a[sortModel[0]?.field] - b[sortModel[0]?.field]) *
            (sortModel[0]?.sort === 'desc' ? -1 : 1)
          );
        } else {
          let valA: string;
          let valB: string;
          if (sortFields.length === 1) {
            valA = toSortableString(a[sortFields[0]]);
            valB = toSortableString(b[sortFields[0]]);
          } else {
            valA = sortFields
              .map((field) => (a[field] ? a[field].toString() : ''))
              .filter(Boolean)
              .join(' ');
            valB = sortFields
              .map((field) => (b[field] ? b[field].toString() : ''))
              .filter(Boolean)
              .join(' ');
          }
          return valA.localeCompare(valB) * (sortModel[0]?.sort === 'desc' ? -1 : 1);
        }
      });
    }
    callback?.(sortArr);
    this.resultData = sortArr;
    return this;
  }
  result() {
    return this.resultData;
  }
}

class DataHelper {
  data(data: TypeData = []) {
    return new DataMethods(data);
  }
}

export const dataHelper = new DataHelper();

export interface ISearchOptions {
  search: string;
  fields: (string | [string, Intl.DateTimeFormatOptions] | [string])[];
  /* fields is a complex field that specifies which fields to sort by and, if the field is a date, how it should be formatted. Example:
  ['orderNo'], - simple field,
  [['orderDate']] - date field with default formatting,
  [['printedDateTime',{ hour: '2-digit',minute: '2-digit',second: '2-digit' }]] - date field with custom formatting */
}
interface ISortOptions {
  sortModel: GridSortModel;
  callback?: (v: TypeData) => void;
}

export type TypeData = Record<string, any>[];
